/**
 * Lane C0-PROPS round 2: ambient effects of the plains kit (the sheets' "effects" family; ideas only): blossom petals
 * drifting down in the pagoda garden and by the homes' plum trees, a few leaves blowing over the training court, and
 * now and then a flock of birds crossing the sky. Only on the 256 m plains (kit/index.ts `kitMap`), only with the
 * ambience setting, a few dozen pixels a frame, deterministic from the world's time (no state, no randomness).
 */
import { hash2, toScreen } from '@nine/shared';
import { settings } from '../../settings';
import { kitMap } from '../kit';
import { registerRenderPass } from './registry';

/** Where petals and leaves fall: map box [x0, x1, y0, y1], how many at once, colours. */
const FALLS: readonly { box: readonly [number, number, number, number]; n: number; cols: readonly string[]; leaf: boolean }[] = [
  { box: [168, 194, 70, 94], n: 28, cols: ['#f2a0b9', '#ffd9e4', '#d9708f'], leaf: false },
  { box: [78, 118, 141, 168], n: 16, cols: ['#f2a0b9', '#ffd9e4'], leaf: false },
  { box: [72, 100, 108, 126], n: 10, cols: ['#c99a30', '#a62a30', '#8f6445'], leaf: true },
];

registerRenderPass({
  id: 'kitAmbient',
  layer: 'world',
  order: 505,
  draw(f) {
    if (!settings.ambience || f.scene || !f.outdoors) return;
    const m = f.world.map;
    if (!kitMap(m)) return;
    const g = f.g;
    const t = f.world.time;
    const o = f.renderer.iso.origin;
    for (const fall of FALLS) {
      const [x0, x1, y0, y1] = fall.box;
      // Skip a fall whose box is off the picture.
      const [ax, ay] = toScreen(x0, y1, o);
      const [bx, by] = toScreen(x1, y0, o);
      if (ax + f.ox > f.W + 40 || bx + f.ox < -40 || by + f.oy - 80 > f.H || ay + f.oy < -40) continue;
      for (let k = 0; k < fall.n; k++) {
        const life = 6 + hash2(k, 3) * 4;
        const ph = (t / life + hash2(k, 7)) % 1;
        const cyc = Math.floor(t / life + hash2(k, 7));
        const gx = x0 + hash2(k, cyc) * (x1 - x0);
        const gy = y0 + hash2(cyc, k + 11) * (y1 - y0);
        const [sx, sy] = toScreen(gx, gy, o);
        const z = (1 - ph) * 70;
        const x = Math.round(sx + f.ox + Math.sin(t * 1.3 + k) * 6 + ph * 18);
        const y = Math.round(sy + f.oy - z);
        g.fillStyle = fall.cols[k % fall.cols.length]!;
        g.globalAlpha = ph < 0.1 ? ph * 10 : ph > 0.9 ? (1 - ph) * 10 : 1;
        g.fillRect(x, y, fall.leaf ? 2 : 1, 1);
        if (!fall.leaf && (k + Math.floor(t * 3)) % 2) g.fillRect(x + 1, y + 1, 1, 1);
      }
    }
    g.globalAlpha = 1;
    // A flock every 40 s, crossing the picture from the lower left to the upper right in about 9 s.
    const cyc = Math.floor(t / 40);
    const ph = (t % 40) / 9;
    if (ph < 1) {
      const y0 = f.H * (0.25 + hash2(cyc, 5) * 0.4);
      for (let b = 0; b < 5; b++) {
        const x = Math.round(-20 + ph * (f.W + 40) - b * 9);
        const y = Math.round(y0 - ph * 90 + (b % 2) * 6 + b * 2);
        const flap = Math.floor(t * 8 + b) % 2;
        g.fillStyle = '#2a2430';
        g.fillRect(x - 2, y - flap, 2, 1);
        g.fillRect(x, y, 1, 1);
        g.fillRect(x + 1, y - flap, 2, 1);
      }
    }
  },
});
